import { useState, useEffect, useCallback } from 'react';
import { Crypter } from '../Functions/Security/Crypter';

interface GroupeData {
  modules: string[];
  planCps: string[];
  groupInfo: {
    codgroup: string;
    lib_codgroup: string;
    niveau: number;
  } | null;
}

interface UseGroupeDataReturn {
  data: GroupeData;
  setModules: (modules: string[]) => void;
  setPlanCps: (planCps: string[]) => void;
  setGroupInfo: (info: GroupeData['groupInfo']) => void;
  resetData: () => void;
  isLoading: boolean;
  error: string | null;
}

const useGroupeData = (): UseGroupeDataReturn => {
  const [data, setData] = useState<GroupeData>({
    modules: [],
    planCps: [],
    groupInfo: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const secureStorageOperation = useCallback(<T,>(operation: () => T, fallback: T): T => {
    try {
      return operation();
    } catch (err) {
      console.error('Storage operation failed:', err);
      setError('Erreur lors de l\'opération de stockage');
      return fallback;
    }
  }, []);

  const setModules = useCallback((modules: string[]) => {
    setData(prev => ({ ...prev, modules }));
    secureStorageOperation(() => {
      const encrypted = Crypter.encrypt(JSON.stringify(modules));
      localStorage.setItem('modules', encrypted);
    }, null);
  }, [secureStorageOperation]);

  const setPlanCps = useCallback((planCps: string[]) => {
    setData(prev => ({ ...prev, planCps }));
    secureStorageOperation(() => {
      const encrypted = Crypter.encrypt(JSON.stringify(planCps));
      localStorage.setItem('groupPlanCp', encrypted);
    }, null);
  }, [secureStorageOperation]);

  const setGroupInfo = useCallback((groupInfo: GroupeData['groupInfo']) => {
    setData(prev => ({ ...prev, groupInfo }));
  }, []);

  const resetData = useCallback(() => {
    setData({
      modules: [],
      planCps: [],
      groupInfo: null
    });
    secureStorageOperation(() => {
      localStorage.removeItem('modules');
      localStorage.removeItem('groupPlanCp');
      localStorage.removeItem('groupModule');
      localStorage.setItem('reset', '1');
    }, null);
    setError(null);
  }, [secureStorageOperation]);

  const loadStoredData = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const storedModules = secureStorageOperation(() => {
      const encrypted = localStorage.getItem('modules');
      return encrypted ? JSON.parse(Crypter.decrypt(encrypted)) : [];
    }, []);

    const storedPlanCps = secureStorageOperation(() => {
      const encrypted = localStorage.getItem('groupPlanCp');
      return encrypted ? JSON.parse(Crypter.decrypt(encrypted)) : [];
    }, []);

    const storedGroupModule = secureStorageOperation(() => {
      const encrypted = localStorage.getItem('groupModule');
      return encrypted ? JSON.parse(Crypter.decrypt(encrypted)) : [];
    }, []);

    setData({
      modules: storedModules,
      planCps: storedPlanCps,
      groupInfo: null
    });

    setIsLoading(false);
  }, [secureStorageOperation]);

  useEffect(() => {
    loadStoredData();
  }, [loadStoredData]);

  return {
    data,
    setModules,
    setPlanCps,
    setGroupInfo,
    resetData,
    isLoading,
    error
  };
};

export default useGroupeData;
